import React, { useState } from 'react';
import { Shield, ArrowRight, Check } from 'lucide-react';
import { useLanguage } from '../components/LanguageContext';
import { SEO } from '../components/SEO';
import type { FormData } from '../types';

export function GetStarted() {
  const { language } = useLanguage();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState<FormData>({
    // Company Info
    companySize: '',
    industry: '',
    location: '',
    
    // Infrastructure
    infrastructure: {
      cloud: [],
      onPremise: false,
      hybrid: false
    },
    technologies: {
      operatingSystems: [],
      databases: [],
      networkDevices: [],
      applications: []
    },
    
    // Services & Compliance
    services: [],
    compliance: [],
    currentSecurity: {
      hasSOC: false,
      hasSIEM: false,
      hasEDR: false,
      hasIDS: false
    },
    
    // Contact Info
    name: '',
    jobTitle: '',
    email: '',
    phone: '',
    preferredContact: '',
    additionalInfo: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (category: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [value]: !(prev[category as keyof typeof prev] as any)[value]
      }
    }));
  };

  const handleArrayToggle = (category: string, value: string) => {
    const [parentKey, childKey] = category.split('.');
    
    if (childKey) {
      setFormData(prev => ({
        ...prev,
        [parentKey]: {
          ...prev[parentKey as keyof typeof prev],
          [childKey]: ((prev[parentKey as keyof typeof prev] as any)[childKey] as string[]).includes(value)
            ? ((prev[parentKey as keyof typeof prev] as any)[childKey] as string[]).filter(item => item !== value)
            : [...((prev[parentKey as keyof typeof prev] as any)[childKey] as string[]), value]
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [category]: (prev[category as keyof typeof prev] as string[]).includes(value)
          ? (prev[category as keyof typeof prev] as string[]).filter(item => item !== value)
          : [...(prev[category as keyof typeof prev] as string[]), value]
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Only proceed if we're on the last step
    if (step !== 4) {
      setStep(step + 1);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Create FormData object
      const formData = new FormData();
      
      // Add form name
      formData.append('form-name', 'contact');
      
      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (typeof value === 'object') {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value.toString());
        }
      });

      // Submit to Netlify
      const response = await fetch('/', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setSubmitStatus('success');
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          companySize: '',
          industry: '',
          location: '',
          infrastructure: {
            cloud: [],
            onPremise: false,
            hybrid: false
          },
          technologies: {
            operatingSystems: [],
            databases: [],
            networkDevices: [],
            applications: []
          },
          services: [],
          compliance: [],
          currentSecurity: {
            hasSOC: false,
            hasSIEM: false,
            hasEDR: false,
            hasIDS: false
          },
          name: '',
          jobTitle: '',
          email: '',
          phone: '',
          preferredContact: '',
          additionalInfo: ''
        });
        setStep(1);
      }, 3000);
    } catch (error) {
      console.error('Failed to submit form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-white font-medium mb-2" htmlFor="companySize">
                Company Size
              </label>
              <select
                id="companySize"
                name="companySize"
                value={formData.companySize}
                onChange={handleInputChange}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              >
                <option value="">Select...</option>
                <option value="1-50">1-50 employees</option>
                <option value="51-200">51-200 employees</option>
                <option value="201-500">201-500 employees</option>
                <option value="501-1000">501-1000 employees</option>
                <option value="1000+">1000+ employees</option>
              </select>
            </div>

            <div>
              <label className="block text-white font-medium mb-2" htmlFor="industry">
                Industry
              </label>
              <select
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              >
                <option value="">Select...</option>
                <option value="finance">Finance & Insurance</option>
                <option value="healthcare">Healthcare</option>
                <option value="technology">Technology</option>
                <option value="manufacturing">Manufacturing</option>
                <option value="retail">Retail</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-white font-medium mb-2" htmlFor="location">
                Region
              </label>
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              >
                <option value="">Select...</option>
                <option value="na">North America</option>
                <option value="eu">Europe</option>
                <option value="asia">Asia Pacific</option>
                <option value="latam">Latin America</option>
                <option value="mea">Middle East & Africa</option>
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">
                Cloud Services
              </h3>
              <div className="space-y-3">
                {['aws', 'azure', 'gcp', 'other'].map(service => (
                  <label key={service} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.infrastructure.cloud.includes(service)}
                      onChange={() => handleArrayToggle('infrastructure.cloud', service)}
                      className="w-4 h-4 text-blue-500 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-300">{service.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-4">
                Environment Type
              </h3>
              <div className="space-y-3">
                {['onPremise', 'hybrid'].map(type => (
                  <label key={type} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.infrastructure[type as keyof typeof formData.infrastructure] as boolean}
                      onChange={() => handleCheckboxChange('infrastructure', type)}
                      className="w-4 h-4 text-blue-500 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-300">
                      {type === 'onPremise' ? 'On-premise' : 'Hybrid'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-4">
                Operating Systems
              </h3>
              <div className="space-y-3">
                {['windows', 'linux', 'macos'].map(os => (
                  <label key={os} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.technologies.operatingSystems.includes(os)}
                      onChange={() => handleArrayToggle('technologies.operatingSystems', os)}
                      className="w-4 h-4 text-blue-500 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-300">{os.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium text-white mb-4">
                Security Services
              </h3>
              <div className="space-y-3">
                {['siem', 'edr', 'threatHunting', 'incidentResponse'].map(service => (
                  <label key={service} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service)}
                      onChange={() => handleArrayToggle('services', service)}
                      className="w-4 h-4 text-blue-500 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-300">
                      {service === 'siem' ? 'SIEM' : 
                       service === 'edr' ? 'EDR/XDR' :
                       service === 'threatHunting' ? 'Threat Hunting' :
                       'Incident Response'}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-4">
                Compliance Standards
              </h3>
              <div className="space-y-3">
                {['gdpr', 'pci', 'hipaa', 'sox', 'iso27001'].map(standard => (
                  <label key={standard} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.compliance.includes(standard)}
                      onChange={() => handleArrayToggle('compliance', standard)}
                      className="w-4 h-4 text-blue-500 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-300">{standard.toUpperCase()}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-4">
                Current Security Solutions
              </h3>
              <div className="space-y-3">
                {Object.keys(formData.currentSecurity).map(solution => (
                  <label key={solution} className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={formData.currentSecurity[solution as keyof typeof formData.currentSecurity]}
                      onChange={() => handleCheckboxChange('currentSecurity', solution)}
                      className="w-4 h-4 text-blue-500 border-gray-700 rounded focus:ring-blue-500"
                    />
                    <span className="text-gray-300">
                      {solution === 'hasSOC' ? 'Internal SOC' :
                       solution === 'hasSIEM' ? 'SIEM' :
                       solution === 'hasEDR' ? 'EDR/XDR' :
                       'IDS/IPS'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-white font-medium mb-2" htmlFor="name">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter your name"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2" htmlFor="jobTitle">
                Job Title
              </label>
              <input
                type="text"
                id="jobTitle"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                placeholder="Your position in the company"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2" htmlFor="email">
                Business Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="youremail@company.com"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2" htmlFor="phone">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Your phone number"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2" htmlFor="preferredContact">
                Preferred Contact Method
              </label>
              <select
                id="preferredContact"
                name="preferredContact"
                value={formData.preferredContact}
                onChange={handleInputChange}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                required
              >
                <option value="">Select...</option>
                <option value="email">Email</option>
                <option value="phone">Phone</option>
              </select>
            </div>

            <div>
              <label className="block text-white font-medium mb-2" htmlFor="additionalInfo">
                Additional Information
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleInputChange}
                placeholder="Other details or specific needs..."
                rows={4}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <SEO 
        title="Get Started"
        description="Configure your tailored security solution"
        keywords={['cybersecurity', 'security services', 'SIEM', 'monitoring', 'threat detection']}
      />

      <div className="hero-pattern py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Shield className="w-16 h-16 text-blue-500 mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white text-center mb-6">
              Get Started with secusoc.io
            </h1>
            <p className="text-xl text-gray-300 text-center">
              Configure your tailored security solution
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-12">
            <div className="flex justify-between">
              {[1, 2, 3, 4].map((stepNum) => (
                <div key={stepNum} className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    stepNum === step
                      ? 'bg-blue-500 text-white'
                      : stepNum < step
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    {stepNum < step ? (
                      <Check className="w-6 h-6" />
                    ) : (
                      stepNum
                    )}
                  </div>
                  <div className="text-sm font-medium text-gray-300">
                    {stepNum === 1 ? 'Company Info' :
                     stepNum === 2 ? 'Infrastructure' :
                     stepNum === 3 ? 'Services' :
                     'Contact'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <form 
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="bg-gray-800/50 rounded-lg p-8"
          >
            <input type="hidden" name="form-name" value="contact" />
            <div hidden>
              <label>
                Don't fill this out if you're human: <input name="bot-field" />
              </label>
            </div>

            {renderStep()}

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="mt-4 p-4 bg-green-500/10 border border-green-500 rounded-lg text-green-400">
                Your request has been sent successfully! We'll be in touch soon.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="mt-4 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-400">
                There was an error sending your request. Please try again or contact us directly.
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="flex items-center px-6 py-2 border border-gray-600 rounded-lg text-white hover:bg-gray-700 transition-colors duration-300"
                  disabled={isSubmitting}
                >
                  <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                  Previous
                </button>
              )}
              <button
                type="submit"
                className="flex items-center px-6 py-2 bg-blue-500 rounded-lg text-white hover:bg-blue-600 transition-colors duration-300 ml-auto disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {step < 4 ? (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  <>
                    {isSubmitting ? 'Sending...' : 'Submit'}
                    {!isSubmitting && <Check className="w-4 h-4 ml-2" />}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}